/**
 * @engine/input — Input Manager
 *
 * Central hub for the input system:
 * - Manages backend and devices
 * - Routes input events to actions
 * - Handles control scheme switching
 */

import type {
  DeviceId,
  DeviceType,
  DeviceInfo,
  InputEvent,
  InputEventValue,
  ControlValue,
  ControlPath,
  InputAssetDef,
  GamepadEventData,
  SensorEventData,
} from './types';
import type { IInputBackend } from './backend/InputBackend';
import type { IInputDevice } from './devices/InputDevice';
import { Keyboard } from './devices/Keyboard';
import { Mouse } from './devices/Mouse';
import { Gamepad } from './devices/Gamepad';
import { Touchscreen, TouchState } from './devices/Touchscreen';
import { Pen } from './devices/Pen';
import { Accelerometer, Gyroscope } from './devices/Sensor';
import { ActionMap } from './actions/ActionMap';
import { ControlScheme } from './actions/ControlScheme';
import { InputAction } from './actions/InputAction';
import { parseControlPath } from './actions/Binding';

/**
 * Input Manager — central input system coordinator.
 */
export class InputManager {
  // ============================================================================
  // Backend
  // ============================================================================

  private _backend: IInputBackend | null = null;
  private backendUnsubscribes: Array<() => void> = [];

  // ============================================================================
  // Devices
  // ============================================================================

  /** All known devices by ID */
  private readonly devices = new Map<DeviceId, IInputDevice>();

  /** Devices by type for quick lookup */
  private readonly devicesByType = new Map<DeviceType, IInputDevice[]>();

  // ============================================================================
  // Actions
  // ============================================================================

  /** All action maps */
  readonly actionMaps = new Map<string, ActionMap>();

  /** All control schemes */
  private readonly controlSchemes = new Map<string, ControlScheme>();

  /** Currently active control scheme */
  private _activeScheme: ControlScheme | null = null;

  // ============================================================================
  // State
  // ============================================================================

  /** Current time (seconds) */
  private _time = 0;

  /** Is the manager initialized */
  private _initialized = false;

  // ============================================================================
  // Device Event Callbacks
  // ============================================================================

  /** Callbacks for device connected events */
  private readonly deviceConnectedCallbacks: Array<(info: DeviceInfo) => void> = [];
  
  /** Callbacks for device disconnected events */
  private readonly deviceDisconnectedCallbacks: Array<(info: DeviceInfo) => void> = [];

  // ============================================================================
  // Lifecycle
  // ============================================================================

  /**
   * Initialize the input manager with a backend.
   */
  async initialize(backend: IInputBackend): Promise<void> {
    if (this._initialized) {
      throw new Error('InputManager already initialized');
    }

    this._backend = backend;

    // Subscribe to backend events
    this.backendUnsubscribes.push(
      backend.onInputEvent(this.handleInputEvent.bind(this)),
      backend.onDeviceConnected(this.handleDeviceConnected.bind(this)),
      backend.onDeviceDisconnected(this.handleDeviceDisconnected.bind(this))
    );

    // Initialize backend
    await backend.initialize();

    // Create devices for already connected devices
    for (const info of backend.getConnectedDevices()) {
      this.createDevice(info);
    }

    this._initialized = true;
  }

  /**
   * Shutdown the input manager.
   */
  shutdown(): void {
    if (!this._initialized) return;

    // Unsubscribe from backend
    for (const unsub of this.backendUnsubscribes) {
      unsub();
    }
    this.backendUnsubscribes = [];

    // Shutdown backend
    this._backend?.shutdown();
    this._backend = null;

    // Clear state
    this.devices.clear();
    this.devicesByType.clear();
    this.actionMaps.clear();
    this.controlSchemes.clear();
    this._activeScheme = null;

    this._initialized = false;
  }

  /**
   * Update input state (call each frame).
   */
  update(time: number): void {
    if (!this._initialized || !this._backend) return;

    this._time = time;

    // Begin frame for all devices
    for (const device of this.devices.values()) {
      device.beginFrame();
    }

    // Begin frame for all action maps
    for (const map of this.actionMaps.values()) {
      map.beginFrame();
    }

    // Poll backend (for gamepad API, etc.)
    this._backend.poll(time);

    // End frame for all devices
    for (const device of this.devices.values()) {
      device.endFrame();
    }

    // End frame for all action maps (pass time for interaction re-evaluation)
    for (const map of this.actionMaps.values()) {
      map.endFrame(time);
    }
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get backend(): IInputBackend | null {
    return this._backend;
  }

  // ============================================================================
  // Device Management
  // ============================================================================

  /**
   * Get a device by ID.
   */
  getDevice<T extends IInputDevice = IInputDevice>(id: DeviceId): T | undefined {
    return this.devices.get(id) as T | undefined;
  }

  /**
   * Get all devices of a type.
   */
  getDevices<T extends IInputDevice = IInputDevice>(type: DeviceType): T[] {
    return (this.devicesByType.get(type) ?? []) as T[];
  }

  /**
   * Get the keyboard (first connected).
   */
  get keyboard(): Keyboard | undefined {
    return this.getDevices<Keyboard>('keyboard')[0];
  }

  /**
   * Get the mouse (first connected).
   */
  get mouse(): Mouse | undefined {
    return this.getDevices<Mouse>('mouse')[0];
  }

  /**
   * Get all gamepads.
   */
  get gamepads(): Gamepad[] {
    return this.getDevices<Gamepad>('gamepad');
  }

  /**
   * Get a specific gamepad.
   */
  getGamepad(index: number = 0): Gamepad | undefined {
    return this.getDevice<Gamepad>(`gamepad/${index}`);
  }

  /**
   * Get touchscreen.
   */
  get touchscreen(): Touchscreen | undefined {
    return this.getDevices<Touchscreen>('touchscreen')[0];
  }

  /**
   * Get pen.
   */
  get pen(): Pen | undefined {
    return this.getDevices<Pen>('pen')[0];
  }

  // ============================================================================
  // Device Event Subscriptions
  // ============================================================================

  /**
   * Subscribe to device connected events.
   * @returns Unsubscribe function
   */
  onDeviceConnected(callback: (info: DeviceInfo) => void): () => void {
    this.deviceConnectedCallbacks.push(callback);
    return () => {
      const idx = this.deviceConnectedCallbacks.indexOf(callback);
      if (idx >= 0) this.deviceConnectedCallbacks.splice(idx, 1);
    };
  }

  /**
   * Subscribe to device disconnected events.
   * @returns Unsubscribe function
   */
  onDeviceDisconnected(callback: (info: DeviceInfo) => void): () => void {
    this.deviceDisconnectedCallbacks.push(callback);
    return () => {
      const idx = this.deviceDisconnectedCallbacks.indexOf(callback);
      if (idx >= 0) this.deviceDisconnectedCallbacks.splice(idx, 1);
    };
  }

  private createDevice(info: DeviceInfo): IInputDevice {
    // Check if device already exists
    if (this.devices.has(info.id)) {
      return this.devices.get(info.id)!;
    }

    // Parse device ID
    const [typeStr, indexStr] = info.id.split('/');
    const type = typeStr as DeviceType;
    const index = parseInt(indexStr, 10);

    // Create device
    let device: IInputDevice;
    switch (type) {
      case 'keyboard':
        device = new Keyboard(index);
        break;
      case 'mouse':
        device = new Mouse(index);
        break;
      case 'gamepad':
        device = new Gamepad(index, info.name, info.vendorId, info.productId);
        break;
      case 'touchscreen':
        device = new Touchscreen(index);
        break;
      case 'pen':
        device = new Pen(index);
        break;
      case 'accelerometer':
        device = new Accelerometer(index);
        break;
      case 'gyroscope':
        device = new Gyroscope(index);
        break;
      default:
        throw new Error(`Unknown device type: ${type}`);
    }

    // Register device
    this.devices.set(info.id, device);

    // Add to type index
    let typeDevices = this.devicesByType.get(type);
    if (!typeDevices) {
      typeDevices = [];
      this.devicesByType.set(type, typeDevices);
    }
    typeDevices.push(device);

    // Update control scheme matching
    this.updateActiveScheme();

    return device;
  }

  private removeDevice(info: DeviceInfo): void {
    const device = this.devices.get(info.id);
    if (!device) return;

    // Remove from main map
    this.devices.delete(info.id);

    // Remove from type index
    const typeDevices = this.devicesByType.get(device.type);
    if (typeDevices) {
      const idx = typeDevices.indexOf(device);
      if (idx >= 0) typeDevices.splice(idx, 1);
    }

    // Update control scheme matching
    this.updateActiveScheme();
  }

  // ============================================================================
  // Input Event Handling
  // ============================================================================

  private handleInputEvent(event: InputEvent): void {
    const device = this.devices.get(event.device);
    if (!device) return;

    // Handle special raw events for certain devices
    if (event.control === '__raw__') {
      this.handleRawEvent(device, event.value, event.time);
      return;
    }

    // For regular control updates, value is ControlValue
    const value = event.value as ControlValue;
    
    // Update device control
    device.updateControl(event.control, value, event.time);

    // Route to actions
    this.routeToActions(event.device, event.control, value, event.time);
  }

  private handleRawEvent(device: IInputDevice, value: InputEventValue, time: number): void {
    // Gamepad raw event
    if (device.type === 'gamepad' && typeof value === 'object' && 'buttons' in value) {
      const gamepad = device as Gamepad;
      const raw = value as GamepadEventData;
      // Cast to browser GamepadButton[] - structurally compatible
      gamepad.updateFromGamepadAPI(
        raw.buttons as unknown as globalThis.GamepadButton[],
        raw.axes,
        time
      );

      // Route stick and button changes to actions
      this.routeToActions(device.id, 'leftStick', gamepad.leftStick.value, time);
      this.routeToActions(device.id, 'rightStick', gamepad.rightStick.value, time);
      this.routeToActions(device.id, 'leftTrigger', gamepad.leftTrigger.isPressed, time);
      this.routeToActions(device.id, 'rightTrigger', gamepad.rightTrigger.isPressed, time);
      // ... other buttons routed automatically
    }

    // Accelerometer raw event
    if (device.type === 'accelerometer') {
      const accel = device as Accelerometer;
      if (typeof value === 'object' && 'accelerationIncludingGravity' in value) {
        const sensorData = value as SensorEventData;
        accel.updateFromDeviceMotion(sensorData, time);
        this.routeToActions(device.id, 'acceleration', accel.acceleration.value, time);
      }
    }

    // Gyroscope raw event
    if (device.type === 'gyroscope') {
      const gyro = device as Gyroscope;
      if (typeof value === 'object' && 'rotationRate' in value) {
        const sensorData = value as SensorEventData;
        gyro.updateFromDeviceMotion(sensorData, time);
        this.routeToActions(device.id, 'angularVelocity', gyro.angularVelocity.value, time);
      }
    }
  }

  private handleDeviceConnected(info: DeviceInfo): void {
    this.createDevice(info);
    // Notify subscribers
    for (const callback of this.deviceConnectedCallbacks) {
      try {
        callback(info);
      } catch (err) {
        console.error('Error in device connected callback:', err);
      }
    }
  }

  private handleDeviceDisconnected(info: DeviceInfo): void {
    this.removeDevice(info);
    // Notify subscribers
    for (const callback of this.deviceDisconnectedCallbacks) {
      try {
        callback(info);
      } catch (err) {
        console.error('Error in device disconnected callback:', err);
      }
    }
  }

  // ============================================================================
  // Action Routing
  // ============================================================================

  private routeToActions(
    deviceId: DeviceId,
    control: string,
    value: ControlValue,
    time: number
  ): void {
    // Parse device type from ID
    const [typeStr] = deviceId.split('/');
    const deviceType = typeStr as DeviceType;

    // Check all action maps
    for (const map of this.actionMaps.values()) {
      if (!map.enabled) continue;

      for (const action of map) {
        if (!action.enabled) continue;

        // Find matching bindings
        for (const binding of action.bindings) {
          if (!binding.active) continue;

          // Check if binding matches this device and control
          if (!binding.matchesDevice(deviceType)) continue;

          // Check control name match
          // Support sub-controls: binding path <Mouse>/scroll/y should match control 'scroll/y'
          const bindingControl = binding.parsed.subControl 
            ? `${binding.parsed.control}/${binding.parsed.subControl}` 
            : binding.parsed.control;
          if (bindingControl !== control) continue;

          // Check scheme match
          if (binding.scheme && this._activeScheme?.name !== binding.scheme) continue;

          // Route to action
          action.updateBinding(binding, value, time, deviceId);
        }
      }
    }
  }

  // ============================================================================
  // Action Map Management
  // ============================================================================

  /**
   * Load an input asset definition.
   */
  loadAsset(asset: InputAssetDef): void {
    // Clear existing
    this.actionMaps.clear();
    this.controlSchemes.clear();

    // Load control schemes
    for (const schemeDef of asset.controlSchemes) {
      const scheme = ControlScheme.fromDefinition(schemeDef);
      this.controlSchemes.set(scheme.name, scheme);
    }

    // Load action maps
    for (const mapDef of asset.actionMaps) {
      const map = ActionMap.fromDefinition(mapDef);
      this.actionMaps.set(map.name, map);
    }

    // Set active scheme
    if (asset.settings?.defaultScheme) {
      this.setActiveScheme(asset.settings.defaultScheme);
    } else {
      this.updateActiveScheme();
    }
  }

  /**
   * Get an action map by name.
   */
  getActionMap(name: string): ActionMap | undefined {
    return this.actionMaps.get(name);
  }

  /**
   * Get an action by path.
   *
   * Two forms are accepted:
   *  - Qualified `"MapName/ActionName"` — looks up the action in that specific map.
   *  - Bare `"ActionName"` — the form scripts use (`isInputPressed('Jump')`). Scans
   *    all loaded maps for an action of that name, preferring ENABLED maps so an
   *    inactive map's stale action never shadows the live one. Without this, bare
   *    names resolved to `actionMaps.get('Jump')` (a map lookup) → always undefined,
   *    which is why InputReceiver-driven scripts saw no input.
   */
  getAction(path: string): InputAction | undefined {
    if (path.includes('/')) {
      const [mapName, actionName] = path.split('/');
      return this.actionMaps.get(mapName)?.getAction(actionName);
    }

    let fallback: InputAction | undefined;
    for (const map of this.actionMaps.values()) {
      const action = map.getAction(path);
      if (!action) continue;
      if (map.enabled) return action;
      fallback ??= action;
    }
    return fallback;
  }

  /**
   * Enable an action map.
   */
  enableActionMap(name: string): void {
    this.actionMaps.get(name)?.enable();
  }

  /**
   * Disable an action map.
   */
  disableActionMap(name: string): void {
    this.actionMaps.get(name)?.disable();
  }

  // ============================================================================
  // Control Scheme Management
  // ============================================================================

  /**
   * Get the active control scheme.
   */
  get activeScheme(): ControlScheme | null {
    return this._activeScheme;
  }

  /**
   * Set the active control scheme by name.
   */
  setActiveScheme(name: string): void {
    const scheme = this.controlSchemes.get(name);
    if (!scheme) {
      console.warn(`Control scheme not found: ${name}`);
      return;
    }

    if (scheme === this._activeScheme) return;

    this._activeScheme = scheme;
    this.updateBindingActivation();
  }

  /**
   * Auto-select the best matching control scheme based on connected devices.
   */
  private updateActiveScheme(): void {
    // If we have an explicit scheme, keep it
    if (this._activeScheme && this._activeScheme.matches(this.devices)) {
      return;
    }

    // Find first matching scheme
    for (const scheme of this.controlSchemes.values()) {
      if (scheme.matches(this.devices)) {
        this._activeScheme = scheme;
        this.updateBindingActivation();
        return;
      }
    }

    // No match — disable all scheme-specific bindings
    this._activeScheme = null;
    this.updateBindingActivation();
  }

  /**
   * Update binding activation based on current scheme.
   */
  private updateBindingActivation(): void {
    for (const map of this.actionMaps.values()) {
      for (const action of map) {
        for (const binding of action.bindings) {
          // Binding is active if:
          // 1. It has no scheme (works with all)
          // 2. Its scheme matches the active scheme
          binding.active = !binding.scheme || binding.scheme === this._activeScheme?.name;
        }
      }
    }
  }

  // ============================================================================
  // Convenience Accessors
  // ============================================================================

  /**
   * Check if a key is pressed.
   */
  isKeyPressed(code: string): boolean {
    return this.keyboard?.isKeyPressed(code) ?? false;
  }

  /**
   * Check if a key was just pressed this frame.
   */
  wasKeyPressed(code: string): boolean {
    return this.keyboard?.wasKeyPressed(code) ?? false;
  }

  /**
   * Check if a mouse button is pressed.
   */
  isMouseButtonPressed(button: string = 'leftButton'): boolean {
    const ctrl = this.mouse?.getControl(button);
    return ctrl?.value === true;
  }

  /**
   * Get mouse position.
   */
  get mousePosition(): { x: number; y: number } {
    return this.mouse?.position.value ?? { x: 0, y: 0 };
  }

  /**
   * Get mouse delta (movement since last frame).
   */
  get mouseDelta(): { x: number; y: number } {
    return this.mouse?.delta.value ?? { x: 0, y: 0 };
  }

  /**
   * Request pointer lock.
   */
  async requestPointerLock(): Promise<boolean> {
    return this._backend?.requestPointerLock?.() ?? false;
  }

  /**
   * Exit pointer lock.
   */
  exitPointerLock(): void {
    this._backend?.exitPointerLock?.();
  }

  /**
   * Check if pointer is locked.
   */
  get isPointerLocked(): boolean {
    return this._backend?.isPointerLocked?.() ?? false;
  }

  /**
   * Trigger haptic feedback on a gamepad.
   */
  triggerHaptic(gamepadIndex: number, intensity: number, duration: number): void {
    this._backend?.triggerHaptic?.(`gamepad/${gamepadIndex}`, intensity, duration);
  }
}

// ============================================================================
// Default Instance
// ============================================================================

/**
 * Default input manager instance.
 */
export const inputManager = new InputManager();
